import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Slider } from "@/components/ui/slider"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useAudio } from "@/contexts/AudioContext"
import { useAuth } from "@/contexts/AuthContext"
import { useOrder } from "@/contexts/OrderContext"
import { CircleGaugeIcon, FastForwardIcon, LoaderCircleIcon, PauseIcon, PlayIcon, RewindIcon, Volume2Icon } from "lucide-react"
import React from "react"
import { useHotkeys } from "react-hotkeys-hook"

export default function ReportPlayer() {
    const {
        loading,
        playing,
        togglePlay,
        audioSettings,
        position,
        seek,
        handleSeekChange,
        skipTime,
        secondsToMinutes,
        handleSpeedChange,
        rate,
        setTime
    } = useAudio()

    const [isSpeedOpen, setIsSpeedOpen] = React.useState(false)
    const closeTimeout = React.useRef(null)

    const { toggleBot } = useAuth()


    const handleOpen = () => {
        if (!loading) {
            if (closeTimeout.current) {
                clearTimeout(closeTimeout.current)
            }
            setIsSpeedOpen(true)
        }
    }

    const handleClose = (time = 150) => {
        if (closeTimeout.current) {
            clearTimeout(closeTimeout.current)
        }

        closeTimeout.current = setTimeout(() => {
            setIsSpeedOpen(false)
        }, time)
    }

    const { order } = useOrder()

    Object.keys(audioSettings.shortcuts).forEach((shortcutKey) => {
        const shortcut = audioSettings.shortcuts[shortcutKey];
        // Bind hotkeys and prevent browser shortcuts
        useHotkeys(shortcut.key, (event) => {
            // Ensure event is not undefined
            if (event && event.key) {
                handleShortcutAction(shortcut.key, event, shortcutKey);
            }
        });
    });

    const handleShortcutAction = (keyCombination, event, shortcutKey) => {
        // List of browser shortcuts to prevent
        if (
            // Browser shortcuts that should be prevented
            (event.ctrlKey && event.key === "p") ||  // Ctrl + P -> Print
            (event.ctrlKey && event.key === "&") ||  // Ctrl + P -> Print
            (event.ctrlKey && event.key === "é") ||  // Ctrl + P -> Print
            (event.ctrlKey && event.key === '"') ||  // Ctrl + P -> Print
            (event.ctrlKey && event.key === "'") ||  // Ctrl + P -> Print
            (event.ctrlKey && event.key === "(") ||  // Ctrl + P -> Print
            (event.ctrlKey && event.key === "-") ||  // Ctrl + P -> Print
            (event.ctrlKey && event.key === "è") ||  // Ctrl + P -> Print
            (event.ctrlKey && event.key === "_") ||  // Ctrl + P -> Print
            (event.ctrlKey && event.key === "ç") ||  // Ctrl + P -> Print
            (event.ctrlKey && event.key === "à") ||  // Ctrl + P -> Print
            (event.ctrlKey && event.key === "t") ||  // Ctrl + T -> Open new tab
            (event.ctrlKey && event.key === "w") ||  // Ctrl + W -> Close tab
            (event.ctrlKey && event.key === "r") ||  // Ctrl + R -> Reload page
            (event.ctrlKey && event.key === "f") ||  // Ctrl + F -> Find on page
            (event.ctrlKey && event.key === "a") ||  // Ctrl + A -> Select all
            (event.ctrlKey && event.key === "l") ||  // Ctrl + A -> Select all
            (event.ctrlKey && event.key === "1") ||  // Ctrl + 1 -> Switch to first tab
            (event.ctrlKey && event.key === "2") ||  // Ctrl + 2 -> Switch to second tab
            (event.ctrlKey && event.key === "3") ||  // Ctrl + 3 -> Switch to third tab
            (event.ctrlKey && event.key === "4") ||  // Ctrl + 4 -> Switch to fourth tab
            (event.ctrlKey && event.key === "5") ||  // Ctrl + 5 -> Switch to fifth tab
            (event.ctrlKey && event.key === "6") ||  // Ctrl + 6 -> Switch to sixth tab
            (event.ctrlKey && event.key === "7") ||  // Ctrl + 7 -> Switch to seventh tab
            (event.ctrlKey && event.key === "8") ||  // Ctrl + 8 -> Switch to eighth tab
            (event.ctrlKey && event.key === "9") ||  // Ctrl + 9 -> Switch to last tab
            (event.ctrlKey && event.key === "k") ||  // Ctrl + 9 -> Switch to last tab
            (event.ctrlKey && event.key === "l") ||  // Ctrl + 9 -> Switch to last tab
            (event.key === "f1") || // Ctrl + F12 -> Open Developer Tools
            (event.key === "f2") || // Ctrl + F12 -> Open Developer Tools
            (event.key === "f3") || // Ctrl + F12 -> Open Developer Tools
            (event.key === "f4") || // Ctrl + F12 -> Open Developer Tools
            (event.key === "f5") || // Ctrl + F12 -> Open Developer Tools
            (event.key === "f6") || // Ctrl + F12 -> Open Developer Tools
            (event.key === "f7") || // Ctrl + F12 -> Open Developer Tools
            (event.key === "f8") || // Ctrl + F12 -> Open Developer Tools
            (event.key === "f9") || // Ctrl + F12 -> Open Developer Tools
            (event.key === "f10") || // Ctrl + F12 -> Open Developer Tools
            (event.key === "f11") || // Ctrl + F12 -> Open Developer Tools
            (event.key === "f12")
        ) {
            event.preventDefault();
        }

        if (shortcutKey === 'play/pause') {
            togglePlay();
        }

        if (shortcutKey === 'skip-backwards') {
            skipTime(-audioSettings.timeJump); // Skip back by 10 seconds

        }

        if (shortcutKey === 'assistant') {
            toggleBot()
        }

        if (shortcutKey === 'skip-forward') {
            skipTime(audioSettings.timeJump); // Skip forward by 10 seconds
        }

        // Handle rewind-start
        if (shortcutKey === 'rewind-start') {
            setTime(0); // Rewind to the start
        }

        // Handle speed-down
        if (shortcutKey === 'speed-down') {
            handleOpen()
            handleClose(1000)

            handleSpeedChange(rate - 0.1); // Slow down the speed
        }

        // Handle speed-up
        if (shortcutKey === 'speed-up') {
            handleOpen()
            handleClose(1000)

            handleSpeedChange(rate + 0.1); // Speed up
        }
    };

    return <div className="flex-grow">
        <div className="flex items-center space-x-1">
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8" disabled={loading} onClick={() => skipTime(-audioSettings.timeJump)}>
                        {<RewindIcon className="h-4 w-4" />}
                    </Button>
                </TooltipTrigger>

                <TooltipContent side="bottom" style={{ padding: '2px 4px' }}>
                    <span className="uppercase" style={{ fontSize: 8 }}>{audioSettings.shortcuts['skip-backwards'].labelKey}</span>
                </TooltipContent>
            </Tooltip>



            <Tooltip>
                <TooltipTrigger asChild>
                    {!loading && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={togglePlay}>
                        {playing ? <PauseIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4" />}
                    </Button>}
                </TooltipTrigger>

                <TooltipContent side="bottom" style={{ padding: '2px 4px' }}>
                    <span className="uppercase" style={{ fontSize: 8 }}>{audioSettings.shortcuts['play/pause'].labelKey}</span>
                </TooltipContent>
            </Tooltip>

            {loading && <Button variant="ghost" size="icon" className="h-8 w-8" disabled={true}>
                <LoaderCircleIcon className="h-4 w-4 animate-spin" />
            </Button>}

            <Tooltip>
                <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => skipTime(audioSettings.timeJump)} disabled={loading}>
                        {<FastForwardIcon className="h-4 w-4" />}
                    </Button>
                </TooltipTrigger>

                <TooltipContent side="bottom" style={{ padding: '2px 4px' }}>
                    <span className="uppercase" style={{ fontSize: 8 }}>{audioSettings.shortcuts['skip-forward'].labelKey}</span>
                </TooltipContent>
            </Tooltip>




            <Popover open={isSpeedOpen} onOpenChange={setIsSpeedOpen}>
                <PopoverTrigger
                    onMouseEnter={handleOpen}
                    onMouseLeave={handleClose}
                >
                    <Button variant="ghost" size="icon" className="h-8 w-8" disabled={loading}>
                        {<CircleGaugeIcon className="h-4 w-4" />}
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    side="bottom"
                    align="start"
                    onMouseEnter={handleOpen}
                    onMouseLeave={handleClose}
                >
                    <div className="flex items-center gap-1.5 whitespace-nowrap">
                        <span className="text-sm text-muted-foreground uppercase">{audioSettings.shortcuts['speed-down'].labelKey}</span>
                        <Slider
                            defaultValue={[1]}
                            max={1.4}
                            min={0.6}
                            step={0.05}
                            value={[rate]}
                            onValueChange={(newRate) => handleSpeedChange(newRate[0])}
                            className="w-full"
                        />
                        <span className="text-sm text-muted-foreground uppercase">{audioSettings.shortcuts['speed-up'].labelKey}</span>
                    </div>
                </PopoverContent>
            </Popover>

            <div className="flex-grow flex items-center space-x-2">
                <span className="mr-1 shrink-0 text-sm text-muted-foreground text-xs">{secondsToMinutes(seek)} / {secondsToMinutes(order.audio.duration)}</span>
                <Slider
                    defaultValue={[0]}
                    max={100}
                    step={0.1}
                    value={[position]}
                    onValueChange={handleSeekChange}
                    disabled={loading}
                    className="w-full"
                />
            </div>
        </div>
    </div>
}